import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addMonthsKey, dayKeyParts, isSameMonth, monthCells, MONTH_NAMES, weekDaysOf } from '../../lib/calendar/zonedCalendar';

/**
 * The rail's mini-month (#677, prototype). Hand-rolled on zonedCalendar rather
 * than `components/ui/calendar.jsx`: that wrapper has no consumers and pins
 * react-day-picker 8, whose peer range stops at React 18 / date-fns 3 (the app
 * is on React 19 / date-fns 4), and it would need its own zone handling.
 *
 * Its ‹ › browse months without moving the grid — the prototype wired them to
 * the grid's own prev/next, which in week view moved the mini-month by a week.
 * Clicking a day moves the grid there.
 */
export function MiniMonth({ anchorDay, todayKey, view, onPickDay }) {
    const [shown, setShown] = useState(anchorDay);
    useEffect(() => { setShown(anchorDay); }, [anchorDay]);

    const p = dayKeyParts(shown);
    const cells = monthCells(shown);
    const week = view === 'week' ? new Set(weekDaysOf(anchorDay)) : null;

    return (
        <div data-testid="calendar-mini-month">
            <div className="flex items-center gap-1.5 mb-2.5">
                <span className="flex-1 text-[13px] font-semibold text-[#0C0A09]" aria-live="polite">{MONTH_NAMES[p.month]} {p.year}</span>
                <button type="button" onClick={() => setShown(addMonthsKey(shown, -1))} aria-label="Previous month" className="w-6 h-6 rounded-[7px] flex items-center justify-center text-[#57504B] hover:bg-[#F2F0EC]">
                    <ChevronLeft className="w-[15px] h-[15px]" />
                </button>
                <button type="button" onClick={() => setShown(addMonthsKey(shown, 1))} aria-label="Next month" className="w-6 h-6 rounded-[7px] flex items-center justify-center text-[#57504B] hover:bg-[#F2F0EC]">
                    <ChevronRight className="w-[15px] h-[15px]" />
                </button>
            </div>
            <div className="grid grid-cols-7 gap-y-px" role="grid" aria-label={`${MONTH_NAMES[p.month]} ${p.year}`}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((h, i) => (
                    <div key={i} className="h-[22px] flex items-center justify-center text-[10.5px] font-semibold text-[#A29B95]" aria-hidden="true">{h}</div>
                ))}
                {cells.map(key => {
                    const cp = dayKeyParts(key);
                    const today = key === todayKey;
                    const dim = !isSameMonth(key, shown);
                    const inWeek = week?.has(key);
                    const selected = key === anchorDay && view !== 'week';
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onPickDay(key)}
                            aria-label={`${cp.day} ${MONTH_NAMES[cp.month]} ${cp.year}`}
                            aria-current={today ? 'date' : undefined}
                            className={`h-[26px] flex items-center justify-center ${inWeek ? 'bg-[#EFEAFE]' : ''}`}
                            data-testid={`mini-day-${key}`}
                        >
                            <span className={`flex items-center justify-center w-[22px] h-[22px] rounded-full text-[11.5px] ${today ? 'bg-[#4D11F6] text-white font-semibold' : selected ? 'ring-1 ring-[#4D11F6] text-[#3A3532]' : dim ? 'text-[#C0BAB4]' : 'text-[#3A3532] hover:bg-[#F2F0EC]'}`}>
                                {cp.day}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
